<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, nextTick, onMounted, reactive, ref, useId, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useForm } from "vee-validate"
import { toTypedSchema } from "@vee-validate/yup"
import * as yup from "yup"
import type { PvSegment } from "./pv-form.types"

// ================================
// I18N
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            pvDim: {
                roofSegment: {
                    roof: "Pan de toit {n}",
                    incomplete: " - Incomplet",
                    menuModify: "Modifier",
                    menuDeactivate: "Désactiver",
                    menuActivate: "Activer",
                    menuDelete: "Supprimer",
                    menuAria: "Actions sur le pan de toit {n}",
                    warningPowerVsArea:
                        "Attention, la valeur de la puissance installée semble élevée. En moyenne, il faut compter 5m² de toiture pour 1 kWc.",
                    sectionRoof: "Propriétés du toit",
                    area: "Aire en m²",
                    angle: "Angle d'inclinaison",
                    aspect: "Orientation",
                    optimalToggle:
                        "Angle d'inclinaison et orientation optimisés",
                    sectionInstall: "Paramètres de la sous-installation",
                    peakpower: "Puissance installée en kWc",
                    tech: "Technologie PV",
                    loss: "Pertes système (%)",
                    lossTooltip: `- Valeur par default 26%
- Pertes onduleurs 10%
- Pertes température 8%
- Pertes câbles et connexion 2%
- Pertes masque 0% (a l'utilisateur de rajouter)
- Pertes faible éclairement 3%
- Pertes liées à la réflectivité 3%`,
                    labelOptimizedAngle: "Optimisé",
                    labelOptimizedWithAngle: "Optimisé • {angle}°",
                    labelOptimizedWithAspect: "Optimisé • {aspect}°",
                    a11yExpand: "Déplier le pan de toit",
                    a11yCollapse: "Replier le pan de toit",
                    validation: {
                        number: "Saisissez un nombre valide.",
                        areaMin: "L’aire doit être positive ou nulle.",
                        angleRequired: "L’angle d’inclinaison est obligatoire.",
                        angleMin: "L’angle doit être entre 0 et 90°.",
                        angleMax: "L’angle doit être entre 0 et 90°.",
                        aspectRequired: "L’orientation est obligatoire.",
                        aspectMin:
                            "L’orientation doit être entre -180 et 180°.",
                        aspectMax:
                            "L’orientation doit être entre -180 et 180°.",
                        peakpowerRequired:
                            "La puissance installée est obligatoire.",
                        peakpowerMin: "La puissance minimale est 0,1 kWc.",
                        peakpowerMax: "La puissance maximale est 1000 kWc.",
                        lossRequired: "Les pertes sont obligatoires.",
                        lossMin: "Les pertes doivent être entre 0 et 100 %.",
                        lossMax: "Les pertes doivent être entre 0 et 100 %.",
                        techRequired: "La technologie est obligatoire.",
                    },
                },
            },
        },
    },
})

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        data: PvSegment
        canDelete?: boolean
        canDisable?: boolean
        isEdit?: boolean
        isNew?: boolean
    }>(),
    {
        canDelete: false,
        canDisable: false,
        isEdit: false,
        isNew: false,
    }
)

const emit = defineEmits<{
    (event: "submit:modification", payload: PvSegment): void
    (event: "submit:new", payload: PvSegment): void
    (event: "delete", payload: PvSegment): void
}>()

// ================================
// ID DE CHAMPS
// ================================
const fieldUid = useId()

// ================================
// MÉTADONNÉES (hors schéma vee-validate)
// ================================
const segmentMeta = reactive({
    id: props.data.id,
    index: props.data.index,
    selected: props.data.selected,
    location: props.data.location,
    isNew: props.data.isNew === true,
})

// ================================
// ANGLES OPTIMAUX & UI
// ================================
const isOpen = ref(props.isNew || props.isEdit)
const isOptimalAngles = ref(props.data.optimalangles === 1)
const manualAngles = ref<{ angle: number; aspect: number }>({
    angle: props.data.angle,
    aspect: props.data.aspect,
})

const roofMenuRef = ref<{ toggle: (e: Event) => void } | null>(null)

/** Évite d’émettre une auto-sauvegarde pendant la réhydratation depuis `props.data`. */
const persistSuppressionCount = ref(0)

const getSystemLossHelp = computed(() => t("pvDim.roofSegment.lossTooltip"))

/**
 * Relie vee-validate à la prop PrimeVue `invalid` (InputNumber, Select).
 */
function getPrimeInvalidFieldProps(state: { errors: string[] }) {
    return {
        props: {
            invalid: state.errors.length > 0,
        },
    }
}

function valuesFromSegment(d: PvSegment) {
    return {
        area: d.area,
        angle: d.angle,
        aspect: d.aspect,
        peakpower: d.peakpower ?? undefined,
        loss: d.loss ?? undefined,
        pvtechchoice: d.pvtechchoice,
    }
}

const getValidationSchema = computed(() =>
    toTypedSchema(
        yup.object({
            area: yup
                .number()
                .typeError(t("pvDim.roofSegment.validation.number"))
                .nullable()
                .min(0, t("pvDim.roofSegment.validation.areaMin"))
                .optional(),
            angle: isOptimalAngles.value
                ? yup
                      .number()
                      .typeError(t("pvDim.roofSegment.validation.number"))
                      .nullable()
                : yup
                      .number()
                      .typeError(t("pvDim.roofSegment.validation.number"))
                      .required(t("pvDim.roofSegment.validation.angleRequired"))
                      .min(0, t("pvDim.roofSegment.validation.angleMin"))
                      .max(90, t("pvDim.roofSegment.validation.angleMax")),
            aspect: isOptimalAngles.value
                ? yup
                      .number()
                      .typeError(t("pvDim.roofSegment.validation.number"))
                      .nullable()
                : yup
                      .number()
                      .typeError(t("pvDim.roofSegment.validation.number"))
                      .required(
                          t("pvDim.roofSegment.validation.aspectRequired")
                      )
                      .min(-180, t("pvDim.roofSegment.validation.aspectMin"))
                      .max(180, t("pvDim.roofSegment.validation.aspectMax")),
            peakpower: yup
                .number()
                .typeError(t("pvDim.roofSegment.validation.number"))
                .required(t("pvDim.roofSegment.validation.peakpowerRequired"))
                .min(0.1, t("pvDim.roofSegment.validation.peakpowerMin"))
                .max(1000, t("pvDim.roofSegment.validation.peakpowerMax")),
            loss: yup
                .number()
                .typeError(t("pvDim.roofSegment.validation.number"))
                .required(t("pvDim.roofSegment.validation.lossRequired"))
                .min(0, t("pvDim.roofSegment.validation.lossMin"))
                .max(100, t("pvDim.roofSegment.validation.lossMax")),
            pvtechchoice: yup
                .string()
                .required(t("pvDim.roofSegment.validation.techRequired")),
        })
    )
)

const { errors, defineField, setFieldValue, meta, validate, resetForm } =
    useForm({
        validationSchema: getValidationSchema,
        initialValues: valuesFromSegment(props.data),
    })

const [modelArea, modelAreaAttrs] = defineField(
    "area",
    getPrimeInvalidFieldProps
)
const [modelAngle, modelAngleAttrs] = defineField(
    "angle",
    getPrimeInvalidFieldProps
)
const [modelAspect, modelAspectAttrs] = defineField(
    "aspect",
    getPrimeInvalidFieldProps
)
const [modelPeakpower, modelPeakpowerAttrs] = defineField(
    "peakpower",
    getPrimeInvalidFieldProps
)
const [modelLoss, modelLossAttrs] = defineField(
    "loss",
    getPrimeInvalidFieldProps
)
const [modelPvtechchoice, modelPvtechchoiceAttrs] = defineField(
    "pvtechchoice",
    getPrimeInvalidFieldProps
)

const getTechOptions = computed(() => [
    { label: "crystSi", value: "crystSi" },
    { label: "CIS", value: "CIS" },
    { label: "CdTe", value: "CdTe" },
])

/**
 * Reconstruit un `PvSegment` à partir des champs validés + métas locales.
 */
function buildPayloadFromState(): PvSegment {
    return {
        id: segmentMeta.id,
        index: segmentMeta.index,
        selected: segmentMeta.selected,
        location: segmentMeta.location,
        isNew: segmentMeta.isNew,
        area: modelArea.value,
        angle: (modelAngle.value as number) ?? 0,
        aspect: (modelAspect.value as number) ?? 0,
        peakpower: modelPeakpower.value,
        loss: modelLoss.value,
        pvtechchoice: String(modelPvtechchoice.value),
        optimalangles: isOptimalAngles.value ? 1 : 0,
    } as PvSegment
}

function applyPropsToForm(d: PvSegment): void {
    persistSuppressionCount.value += 1
    resetForm({ values: valuesFromSegment(d) })
    isOptimalAngles.value = d.optimalangles === 1
    manualAngles.value = { angle: d.angle, aspect: d.aspect }
    Object.assign(segmentMeta, {
        id: d.id,
        index: d.index,
        selected: d.selected,
        location: d.location,
        isNew: d.isNew === true,
    })
    void nextTick(() => {
        persistSuppressionCount.value -= 1
        void validate()
    })
}

watch(
    () => props.data,
    (value) => {
        applyPropsToForm(value)
    },
    { deep: true }
)

watch(
    () => [modelAngle.value, modelAspect.value],
    ([angle, aspect]) => {
        if (!isOptimalAngles.value) {
            manualAngles.value = {
                angle: (angle as number) ?? 0,
                aspect: (aspect as number) ?? 0,
            }
        }
    }
)

watch(
    () => isOptimalAngles.value,
    (value) => {
        if (value) {
            setFieldValue("angle", props.data.angle ?? manualAngles.value.angle)
            setFieldValue(
                "aspect",
                props.data.aspect ?? manualAngles.value.aspect
            )
        } else {
            setFieldValue("angle", manualAngles.value.angle)
            setFieldValue("aspect", manualAngles.value.aspect)
        }
        void nextTick(() => validate())
    }
)

/**
 * Pousse l’état courant vers le parent lorsque le formulaire est valide (champs ou angles optimaux).
 */
async function persistFormIfValid(): Promise<void> {
    if (persistSuppressionCount.value > 0) {
        return
    }
    const { valid } = await validate()
    if (!valid) {
        return
    }
    const payload = buildPayloadFromState()
    if (segmentMeta.isNew) {
        emit("submit:new", payload)
    } else {
        emit("submit:modification", payload)
    }
}

watch(
    () => [
        modelArea.value,
        modelAngle.value,
        modelAspect.value,
        modelPeakpower.value,
        modelLoss.value,
        modelPvtechchoice.value,
        isOptimalAngles.value,
    ],
    () => {
        void persistFormIfValid()
    },
    { flush: "post" }
)

// ================================
// COMPUTED
// ================================
const getSegmentTitle = computed(() =>
    t("pvDim.roofSegment.roof", { n: segmentMeta.index + 1 })
)

/**
 * Bandeau « Incomplet » : aligné sur l’invalidité vee-validate, masqué si le pan est désactivé.
 */
const getShowIncomplete = computed((): boolean => {
    if (props.canDisable && segmentMeta.selected === false) {
        return false
    }
    return !meta.value.valid
})

const getOptimizedAngleLabel = computed(() =>
    props.data.angle != null
        ? t("pvDim.roofSegment.labelOptimizedWithAngle", {
              angle: props.data.angle,
          })
        : t("pvDim.roofSegment.labelOptimizedAngle")
)

const getOptimizedAspectLabel = computed(() =>
    props.data.aspect != null
        ? t("pvDim.roofSegment.labelOptimizedWithAspect", {
              aspect: props.data.aspect,
          })
        : t("pvDim.roofSegment.labelOptimizedAngle")
)

const getRoofMenuItems = computed(
    (): { label: string; command: () => void }[] => {
        const out: { label: string; command: () => void }[] = [
            {
                label: t("pvDim.roofSegment.menuModify"),
                command: () => onMenuOpenEdit(),
            },
        ]
        if (props.canDisable) {
            out.push({
                label: segmentMeta.selected
                    ? t("pvDim.roofSegment.menuDeactivate")
                    : t("pvDim.roofSegment.menuActivate"),
                command: () => onMenuToggleEnabled(),
            })
        }
        if (props.canDelete) {
            out.push({
                label: t("pvDim.roofSegment.menuDelete"),
                command: () => onClickDelete(),
            })
        }
        return out
    }
)

/**
 * Message d’avertissement si la puissance dépasse ~1 kWc pour 5 m² de toit (aligné legacy).
 */
const getPowerVsRoofAreaWarning = computed((): string | null => {
    const rawArea = modelArea.value
    const rawPeak = modelPeakpower.value
    if (rawArea == null || rawPeak == null) {
        return null
    }
    const area = Number(rawArea)
    const peak = Number(rawPeak)
    if (Number.isNaN(area) || Number.isNaN(peak) || area <= 0) {
        return null
    }
    if (peak > area / 5) {
        return t("pvDim.roofSegment.warningPowerVsArea")
    }
    return null
})

// ================================
// INTERACTIONS
// ================================
function onMenuOpenEdit(): void {
    isOpen.value = true
}

function onClickToggleAccordion(): void {
    isOpen.value = !isOpen.value
}

function onMenuToggleEnabled(): void {
    if (!props.canDisable) {
        return
    }
    segmentMeta.selected = !segmentMeta.selected
    emit("submit:modification", buildPayloadFromState())
}

function onToggleRoofMenu(event: Event): void {
    roofMenuRef.value?.toggle(event)
}

function onCheckboxChange(): void {
    if (!props.canDisable) {
        return
    }
    emit("submit:modification", buildPayloadFromState())
}

function onClickDelete(): void {
    emit("delete", buildPayloadFromState())
}

onMounted(() => {
    void validate()
})
</script>

<template>
    <article class="pv-form-roof-segment border-b border-surface-200 py-4">
        <header class="flex items-center justify-between gap-3">
            <h2
                class="min-w-0 flex flex-1 items-center gap-2 text-sm font-medium text-primary"
            >
                <input
                    v-if="canDisable"
                    v-model="segmentMeta.selected"
                    type="checkbox"
                    class="h-3.5 w-3.5 shrink-0 rounded border-surface-300 text-primary"
                    :aria-label="getSegmentTitle"
                    @change="onCheckboxChange"
                />
                <span class="min-w-0">
                    <span>{{ getSegmentTitle }}</span>
                    <span v-if="getShowIncomplete" class="text-surface-500">
                        {{ t("pvDim.roofSegment.incomplete") }}
                    </span>
                </span>
            </h2>

            <div class="flex shrink-0 items-center gap-1">
                <BaseButton
                    type="button"
                    text
                    size="small"
                    class="!h-8 w-8 !p-0"
                    :aria-label="
                        isOpen
                            ? t('pvDim.roofSegment.a11yCollapse')
                            : t('pvDim.roofSegment.a11yExpand')
                    "
                    @click="onClickToggleAccordion"
                >
                    <i
                        class="pi pi-chevron-down transition-transform duration-200"
                        :class="isOpen ? 'rotate-180' : ''"
                    />
                </BaseButton>
                <BaseButton
                    type="button"
                    text
                    size="small"
                    icon="pi pi-ellipsis-v"
                    class="!h-8 w-8 !p-0"
                    :aria-label="
                        t('pvDim.roofSegment.menuAria', {
                            n: segmentMeta.index + 1,
                        })
                    "
                    @click="onToggleRoofMenu"
                />
                <BaseMenu
                    ref="roofMenuRef"
                    :model="getRoofMenuItems"
                    :popup="true"
                />
            </div>
        </header>

        <div v-if="isOpen" class="mt-4 flex flex-col gap-6">
            <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
                <section class="flex flex-col gap-4">
                    <h3 class="text-sm font-medium text-surface-800">
                        {{ t("pvDim.roofSegment.sectionRoof") }}
                    </h3>

                    <BaseInputWrapper
                        :input-id="`${fieldUid}-area`"
                        :label="t('pvDim.roofSegment.area')"
                        :error="errors.area"
                        suffix="m²"
                        unfloat
                    >
                        <BaseInputNumber
                            v-model="modelArea"
                            v-bind="modelAreaAttrs"
                            :input-id="`${fieldUid}-area`"
                            class="w-full"
                            :disabled="!isOpen"
                            :min="0"
                            :min-fraction-digits="0"
                            :max-fraction-digits="3"
                            :step="0.1"
                            :show-buttons="false"
                        />
                    </BaseInputWrapper>

                    <BaseInputWrapper
                        :input-id="`${fieldUid}-angle`"
                        :label="t('pvDim.roofSegment.angle')"
                        :error="errors.angle"
                        suffix="°"
                        unfloat
                    >
                        <BaseInputNumber
                            v-model="modelAngle"
                            v-bind="modelAngleAttrs"
                            :input-id="`${fieldUid}-angle`"
                            class="w-full"
                            :disabled="!isOpen || isOptimalAngles"
                            :placeholder="
                                isOptimalAngles
                                    ? getOptimizedAngleLabel
                                    : undefined
                            "
                            :min="0"
                            :max="90"
                            :min-fraction-digits="0"
                            :max-fraction-digits="3"
                            :step="0.001"
                            :show-buttons="false"
                        />
                    </BaseInputWrapper>

                    <BaseInputWrapper
                        :input-id="`${fieldUid}-aspect`"
                        :label="t('pvDim.roofSegment.aspect')"
                        :error="errors.aspect"
                        suffix="°"
                        unfloat
                    >
                        <BaseInputNumber
                            v-model="modelAspect"
                            v-bind="modelAspectAttrs"
                            :input-id="`${fieldUid}-aspect`"
                            class="w-full"
                            :disabled="!isOpen || isOptimalAngles"
                            :placeholder="
                                isOptimalAngles
                                    ? getOptimizedAspectLabel
                                    : undefined
                            "
                            :min="-180"
                            :max="180"
                            :min-fraction-digits="0"
                            :max-fraction-digits="3"
                            :step="0.001"
                            :show-buttons="false"
                        />
                    </BaseInputWrapper>

                    <label
                        class="inline-flex select-none items-center gap-3 text-sm"
                        :class="isOpen ? 'cursor-pointer' : 'cursor-default'"
                    >
                        <input
                            v-model="isOptimalAngles"
                            :disabled="!isOpen"
                            type="checkbox"
                            class="peer sr-only"
                        />
                        <span
                            class="relative h-6 w-11 rounded-full border transition-colors duration-200 peer-focus:outline-none"
                            :class="
                                isOptimalAngles
                                    ? 'border-primary bg-primary'
                                    : 'border-surface-300 bg-surface-200'
                            "
                        >
                            <span
                                class="absolute top-[2px] left-[2px] h-5 w-5 rounded-full bg-white transition-transform duration-200"
                                :class="isOptimalAngles ? 'translate-x-5' : ''"
                            />
                        </span>
                        <span class="text-surface-700">{{
                            t("pvDim.roofSegment.optimalToggle")
                        }}</span>
                    </label>
                </section>

                <section class="flex flex-col gap-4">
                    <h3 class="text-sm font-medium text-surface-800">
                        {{ t("pvDim.roofSegment.sectionInstall") }}
                    </h3>

                    <BaseInputWrapper
                        :input-id="`${fieldUid}-peak`"
                        :label="t('pvDim.roofSegment.peakpower')"
                        :error="errors.peakpower"
                        unfloat
                    >
                        <BaseInputNumber
                            v-model="modelPeakpower"
                            v-bind="modelPeakpowerAttrs"
                            :input-id="`${fieldUid}-peak`"
                            class="w-full"
                            :disabled="!isOpen"
                            :min="0.1"
                            :max="1000"
                            :min-fraction-digits="1"
                            :max-fraction-digits="2"
                            :step="0.1"
                            :show-buttons="false"
                        />
                    </BaseInputWrapper>

                    <BaseInputWrapper
                        :input-id="`${fieldUid}-tech`"
                        :label="t('pvDim.roofSegment.tech')"
                        :error="errors.pvtechchoice"
                        unfloat
                    >
                        <BaseSelect
                            v-model="modelPvtechchoice"
                            v-bind="modelPvtechchoiceAttrs"
                            :input-id="`${fieldUid}-tech`"
                            class="w-full"
                            :options="getTechOptions"
                            option-label="label"
                            option-value="value"
                            :disabled="!isOpen"
                        />
                    </BaseInputWrapper>

                    <BaseInputWrapper
                        :input-id="`${fieldUid}-loss`"
                        label=""
                        :error="errors.loss"
                        suffix="%"
                        unfloat
                    >
                        <template #header>
                            <div
                                class="mb-2 flex items-center gap-2 text-sm text-gray-500"
                            >
                                <span>{{ t("pvDim.roofSegment.loss") }}</span>
                                <i
                                    v-tooltip.top="getSystemLossHelp"
                                    class="pi pi-info-circle cursor-help text-xs text-surface-500"
                                />
                            </div>
                        </template>
                        <BaseInputNumber
                            v-model="modelLoss"
                            v-bind="modelLossAttrs"
                            :input-id="`${fieldUid}-loss`"
                            class="w-full"
                            :disabled="!isOpen"
                            :min="0"
                            :max="100"
                            :min-fraction-digits="0"
                            :max-fraction-digits="3"
                            :step="0.001"
                            :show-buttons="false"
                        />
                    </BaseInputWrapper>
                </section>
            </div>

            <BaseMessage
                v-if="getPowerVsRoofAreaWarning"
                severity="warn"
                :closable="false"
                class="w-full"
                size="small"
            >
                {{ getPowerVsRoofAreaWarning }}
            </BaseMessage>
        </div>
    </article>
</template>
